import { Button } from '@workspace/ui/components/button';
import { ArrowRight, Briefcase, Building2, User } from 'lucide-react';
import { useState } from 'react';
import { m } from '~/paraglide/messages.js';
import type { AccountType } from '../-schemas/onboarding-schema';

type Props = {
  onSelect: (type: AccountType) => void;
};

type OptionCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
};

function OptionCard({
  icon,
  title,
  description,
  isSelected,
  onClick,
}: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex items-center gap-4 rounded-xl border p-5 text-left transition-all hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-muted-foreground/30'
      }`}
    >
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-colors ${
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground group-hover:text-foreground'
        }`}
      >
        {icon}
      </div>
      <div className="flex-1 space-y-1">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div
        className={`h-5 w-5 shrink-0 rounded-full border-2 transition-all ${
          isSelected
            ? 'border-primary bg-primary'
            : 'border-muted-foreground/30'
        }`}
      >
        {isSelected && (
          <div className="flex h-full w-full items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-primary-foreground" />
          </div>
        )}
      </div>
    </button>
  );
}

export function AccountTypeSelection({ onSelect }: Props) {
  const [selectedType, setSelectedType] = useState<AccountType | null>(null);

  const handleContinue = () => {
    if (selectedType) {
      onSelect(selectedType);
    }
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="space-y-3">
        <OptionCard
          icon={<Building2 className="h-6 w-6" />}
          title={m['onboarding.account_type.business_title']()}
          description={m['onboarding.account_type.business_description']()}
          isSelected={selectedType === 'business'}
          onClick={() => setSelectedType('business')}
        />
        <OptionCard
          icon={<Briefcase className="h-6 w-6" />}
          title={m['onboarding.account_type.freelancer_title']()}
          description={m['onboarding.account_type.freelancer_description']()}
          isSelected={selectedType === 'freelancer'}
          onClick={() => setSelectedType('freelancer')}
        />
        <OptionCard
          icon={<User className="h-6 w-6" />}
          title={m['onboarding.account_type.individual_title']()}
          description={m['onboarding.account_type.individual_description']()}
          isSelected={selectedType === 'individual'}
          onClick={() => setSelectedType('individual')}
        />
      </div>

      <Button
        onClick={handleContinue}
        disabled={!selectedType}
        className="flex w-full items-center justify-center gap-2 py-6 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
      >
        {m['common.continue']()}
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  );
}
